import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useGmbTechnicalPlan, useGmbContentPlan, useGmbGoals, useUpdateContentPostStatus, useUpdateTaskStatus, useGenerateTaskDetails } from '../hooks/useGmbStrategy';
import type { ActionPlanTask, ContentCalendarItem, GmbGoal } from '../types';
import DescriptionKeywordGuide from './DescriptionKeywordGuide';

const FREQUENCY_LABELS: Record<string, string> = { weekly: 'Weekly', monthly: 'Monthly' };

// Business-specific steps, linked goal and website actions for a task (D-205).
function TaskDetails({ details }: { details: ActionPlanTask['details'] }) {
  if (!details || !details.steps?.length) return null;
  return (
    <div className="mt-2 rounded-md border border-violet-100 bg-violet-50/50 p-2.5 text-xs text-gray-700 dark:border-violet-900/50 dark:bg-violet-950/20 dark:text-gray-300">
      {details.linked_goal && (
        <span className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
          <i className="ri-flag-line"></i>
          Moves goal: {details.linked_goal}
        </span>
      )}
      <ol className="list-decimal space-y-0.5 pl-4">
        {details.steps.map((step) => <li key={step}>{step}</li>)}
      </ol>
      {details.website_actions && details.website_actions.length > 0 && (
        <div className="mt-1.5">
          <span className="font-semibold">On your website:</span>
          <ul className="list-disc pl-4">
            {details.website_actions.map((action) => <li key={action}>{action}</li>)}
          </ul>
        </div>
      )}
      {details.why && <p className="mt-1.5 italic text-gray-500 dark:text-gray-400">{details.why}</p>}
    </div>
  );
}

interface ActionPlanProps {
  onTooltipShow: (title: string, content: string) => void;
}

const KPI_ACTIONS: Record<string, string[]> = {
  keyword_rank: [
    'Confirm the primary and secondary GBP categories match the services customers search for, then complete the relevant services and products.',
    'Use priority local keywords naturally in the profile description, service details, and weekly Google posts; avoid keyword stuffing.',
    'Create or improve a website landing page for each priority service and service area, with consistent business name, address, and phone details.',
    'Track the same local search terms monthly and adjust categories, page content, and posts based on ranking changes.',
  ],
  website_clicks: [
    'Verify the GBP website link opens the correct, mobile-friendly service or location page and uses a working HTTPS URL.',
    'Make the destination page fast and clear, with the relevant service, location, contact details, and a prominent next-step call to action.',
    'Add tagged campaign parameters to the GBP link and review website analytics monthly to identify visits and conversions.',
    'Publish a weekly GBP post linking to a useful service, offer, or location page.',
  ],
  calls: [
    'Confirm the primary phone number is correct, monitored during listed hours, and consistent with the website and directories.',
    'Set accurate business hours and special hours so callers know when the team is available.',
    'Add a clear call-to-action to relevant website pages and GBP posts, then return missed calls promptly.',
    'Review call volume and missed-call patterns monthly; update staffing or call handling where needed.',
  ],
  directions: [
    'Verify the GBP street address and map pin identify the customer entrance, not a nearby or rear location.',
    'Keep the address and service-area details consistent on the website and major directories.',
    'Add clear arrival, parking, entrance, or landmark instructions to the website location page and profile where supported.',
    'Review direction requests alongside local activity and correct any customer-reported navigation confusion.',
  ],
  whatsapp: [
    'Enable the correct WhatsApp contact link on the business profile and test it on both mobile and desktop.',
    'Set a response owner and target response time; use a concise welcome message that asks what service or location the customer needs.',
    'Check WhatsApp inquiries daily and record qualified leads so unanswered conversations are followed up.',
    'Mention WhatsApp as a contact option on relevant website pages and posts, without replacing required phone or emergency contacts.',
  ],
  messages: [
    'Enable the profile messaging feature where available and test that messages reach the team responsible for customer inquiries.',
    'Set coverage hours, a response owner, and saved replies for common questions about services, pricing, and availability.',
    'Monitor unanswered messages daily and follow up promptly when a customer needs a booking or detailed assistance.',
    'Review message volume and response times monthly; update staffing or saved answers to reduce delays.',
  ],
  review_count: [
    'After a completed service, invite the customer to leave an honest review using the direct GBP review link.',
    'Share the review link through normal customer follow-up channels, such as email or SMS; do not offer incentives or selectively solicit only positive reviews.',
    'Respond professionally to new reviews and address service issues with a clear offline resolution process.',
    'Track new reviews monthly and train customer-facing staff to make the request consistently.',
  ],
  avg_rating: [
    'Read each new review, identify recurring service issues, and assign an owner and due date for corrective actions.',
    'Respond to negative feedback calmly and specifically; invite the reviewer to continue the resolution privately without disclosing personal information.',
    'Recognize patterns in positive feedback and reinforce the service practices customers value.',
    'Request honest feedback consistently from all eligible customers; never offer incentives or pressure customers to change ratings.',
  ],
  default: [
    'Review the latest Google Business Profile performance and identify the actions most closely related to this KPI.',
    'Keep profile details, website information, and customer contact paths accurate and consistent.',
    'Assign an owner and a regular review cadence, then compare progress with the goal target each month.',
  ],
};

export default function ActionPlan({ onTooltipShow }: ActionPlanProps) {
  const [activeTab, setActiveTab] = useState<'technical' | 'content' | 'kpi'>('technical');
  const [setupFilter, setSetupFilter] = useState<'setup' | 'maintenance'>('setup');
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  const { data: techPlanData, isLoading: techLoading, error: techError, refetch: refetchTech } = useGmbTechnicalPlan();
  const { data: contentPlanData, isLoading: contentLoading, error: contentError, refetch: refetchContent } = useGmbContentPlan();
  const { data: goalsData, isLoading: goalsLoading, error: goalsError, refetch: refetchGoals } = useGmbGoals();
  const updateContentStatusMutation = useUpdateContentPostStatus();
  const updateTaskStatusMutation = useUpdateTaskStatus();
  const generateDetailsMutation = useGenerateTaskDetails();
  const detailsRequested = useRef(false);

  // Older plans have no business-specific steps yet: request them once.
  useEffect(() => {
    const plan: any = techPlanData?.data;
    if (!plan || detailsRequested.current) return;
    const tasks: ActionPlanTask[] = [...(plan.checklist || []), ...(plan.maintenance || [])];
    if (tasks.length > 0 && tasks.some((task) => !task.details)) {
      detailsRequested.current = true;
      generateDetailsMutation.mutate(false);
    }
  }, [techPlanData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (techError || contentError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800">Failed to load action plan</h3>
        <p className="text-xs text-red-600 mt-1">{(techError || contentError)?.message}</p>
        <button onClick={() => { refetchTech(); refetchContent(); }} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const techPlan = techPlanData?.data || {};
  const contentPlan = contentPlanData?.data || {};

  const technicalChecklist: ActionPlanTask[] = techPlan.checklist || [];
  const maintenanceTasks: ActionPlanTask[] = techPlan.maintenance || [];
  const contentCalendar: ContentCalendarItem[] = contentPlan.calendar || [];
  const goals: GmbGoal[] = goalsData?.data?.goals || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>;
      case 'in_progress': return <i className="ri-time-line text-amber-500 text-lg"></i>;
      default: return <i className="ri-checkbox-blank-circle-line text-gray-300 dark:text-gray-600 text-lg"></i>;
    }
  };

  const getPostTypeClass = (type: string) => {
    switch (type) {
      case 'Offer': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
      case "What's New": return 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300';
      case 'Event': return 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
    }
  };

  const INTENT_LABELS: Record<string, string> = {
    introduction: 'Business Introduction/Awareness',
    educational: 'Educational Tips',
    offer: 'Promotional Offer',
    testimonial: 'Customer Testimonial',
    product: 'Product Highlight',
    behind_scenes: 'Behind the Scenes',
    event: 'Event/Seasonal',
  };
  const formatIntent = (intent: string) => {
    if (!intent) return '—';
    return INTENT_LABELS[intent] || intent.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const scheduleContentPost = (row: ContentCalendarItem) => {
    if (!row.id) return;
    updateContentStatusMutation.mutate({ postId: row.id, status: 'scheduled' });
  };

  const TASK_STATUS_CYCLE: Record<string, string> = {
    pending: 'in_progress',
    in_progress: 'completed',
    completed: 'pending',
  };

  const TASK_STATUS_TOAST: Record<string, string> = {
    pending: 'Reset to pending',
    in_progress: 'Marked as in progress',
    completed: 'Marked as complete',
  };

  const cycleTaskStatus = (task: ActionPlanTask) => {
    const next = TASK_STATUS_CYCLE[task.status] || 'pending';
    setUpdatingTaskId(task.task_id);
    updateTaskStatusMutation.mutate(
      { taskId: task.task_id, status: next },
      {
        onSuccess: () => toast.success(TASK_STATUS_TOAST[next]),
        onSettled: () => setUpdatingTaskId(null),
      }
    );
  };

  const getTaskStatus = (task: ActionPlanTask) => task.status;

  const completed = technicalChecklist.filter((c) => getTaskStatus(c) === 'completed').length;
  const progress = technicalChecklist.length > 0 ? (completed / technicalChecklist.length) * 100 : 0;

  const maintCompleted = maintenanceTasks.filter((c) => getTaskStatus(c) === 'completed').length;
  const maintProgress = maintenanceTasks.length > 0 ? (maintCompleted / maintenanceTasks.length) * 100 : 0;

  if (techLoading || contentLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md w-28 animate-pulse"></div>
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md w-28 animate-pulse"></div>
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md w-32 animate-pulse"></div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-5"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('technical')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'technical'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Technical Plan
        </button>
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'content'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Content Plan
        </button>
        <button
          onClick={() => setActiveTab('kpi')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'kpi'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          KPI Action Plan
        </button>
      </div>

      {activeTab === 'technical' && (
        <div className="space-y-6">
          <DescriptionKeywordGuide />
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setSetupFilter('setup')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    setupFilter === 'setup'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  One-Time Setup
                </button>
                <button
                  onClick={() => setSetupFilter('maintenance')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    setupFilter === 'maintenance'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Regular Maintenance
                </button>
              </div>
              <button
                onClick={() => onTooltipShow('Technical Action Plan', 'One-time setup tasks and regular maintenance activities to keep your Google Business Profile fully optimized. Complete setup tasks first, then maintain weekly.')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="More info"
              >
                <i className="ri-information-line text-lg"></i>
              </button>
            </div>

            {generateDetailsMutation.isPending && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-xs text-violet-800 dark:border-violet-900 dark:bg-violet-950/20 dark:text-violet-300">
                <i className="ri-loader-4-line animate-spin"></i>
                Preparing step-by-step actions for your business...
              </div>
            )}

            {setupFilter === 'setup' && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{completed}/{technicalChecklist.length} complete</p>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-5">
                  <div className="h-full rounded-full bg-violet-500 transition-all duration-500" style={{ width: `${progress}%` }}></div>
                </div>
                {technicalChecklist.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <i className="ri-task-line text-4xl text-gray-300 dark:text-gray-600 mb-3 block"></i>
                    <p className="text-sm">No technical tasks yet. Run strategy generation to create your action plan.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {technicalChecklist.map((item) => {
                      const taskStatus = getTaskStatus(item);
                      const isUpdating = updatingTaskId === item.task_id;
                      const hint = taskStatus === 'completed' ? 'Click to reset' : taskStatus === 'in_progress' ? 'Click to complete' : 'Click to start';
                      return (
                        <button
                          key={item.task_id}
                          onClick={() => !isUpdating && cycleTaskStatus(item)}
                          disabled={isUpdating}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left group ${isUpdating ? 'opacity-60 cursor-wait' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                        >
                          {isUpdating ? (
                            <i className="ri-loader-4-line text-violet-500 text-lg animate-spin"></i>
                          ) : (
                            getStatusIcon(taskStatus)
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm block ${taskStatus === 'completed' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                              {item.task_name}
                            </span>
                            {item.description && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 block mt-0.5">
                                {item.description}
                              </span>
                            )}
                            <TaskDetails details={item.details} />
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                              {hint}
                            </span>
                          </div>
                          {taskStatus === 'in_progress' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              In Progress
                            </span>
                          )}
                          {taskStatus === 'completed' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                              Complete
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {setupFilter === 'maintenance' && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{maintCompleted}/{maintenanceTasks.length} complete</p>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-5">
                  <div className="h-full rounded-full bg-violet-500 transition-all duration-500" style={{ width: `${maintProgress}%` }}></div>
                </div>
                {maintenanceTasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <i className="ri-calendar-check-line text-4xl text-gray-300 dark:text-gray-600 mb-3 block"></i>
                    <p className="text-sm">No maintenance tasks yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {maintenanceTasks.map((task) => {
                      const taskStatus = getTaskStatus(task);
                      const isUpdating = updatingTaskId === task.task_id;
                      const hint = taskStatus === 'completed' ? 'Click to reset' : taskStatus === 'in_progress' ? 'Click to complete' : 'Click to start';
                      return (
                        <button
                          key={task.task_id}
                          onClick={() => !isUpdating && cycleTaskStatus(task)}
                          disabled={isUpdating}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left group ${isUpdating ? 'opacity-60 cursor-wait' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                        >
                          {isUpdating ? (
                            <i className="ri-loader-4-line text-violet-500 text-lg animate-spin"></i>
                          ) : (
                            getStatusIcon(taskStatus)
                          )}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm block ${taskStatus === 'completed' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                              {task.task_name}
                              {task.frequency && FREQUENCY_LABELS[task.frequency] && (
                                <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 no-underline dark:bg-gray-700 dark:text-gray-400">
                                  {FREQUENCY_LABELS[task.frequency]}
                                </span>
                              )}
                            </span>
                            {task.description && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 block mt-0.5">
                                {task.description}
                              </span>
                            )}
                            <TaskDetails details={task.details} />
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                              {hint}
                            </span>
                          </div>
                          {taskStatus === 'in_progress' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              In Progress
                            </span>
                          )}
                          {taskStatus === 'completed' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                              Complete
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">4-Week Content Calendar</h3>
              <button
                onClick={() => onTooltipShow('Content Calendar', "Once generated, your 4-week calendar is stored as editable draft posts. Review, edit, approve, reschedule, or publish them here — approved posts move into Post Scheduler (under Implementation) for scheduling and publishing to your Google Business Profile.")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="More info"
              >
                <i className="ri-information-line text-lg"></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              Posts start here as drafts. Mark a post ready to move it toward publishing — track its progress in Post Scheduler under Implementation.
            </p>
            {contentCalendar.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-article-line text-4xl text-gray-300 dark:text-gray-600 mb-3 block"></i>
                <p className="text-sm">No content plan yet. Run strategy generation to create your content calendar.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Week</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Date</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Content Intent</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Post Type</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Title</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">CTA</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Keyword</th>
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contentCalendar.map((row, idx) => {
                      const isDraft = row.status === 'draft';
                      return (
                        <tr key={row.id || idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="py-3 px-3 text-sm text-gray-900 dark:text-white">Week {row.week || idx + 1}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 dark:text-gray-400">{row.date}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 dark:text-gray-400">{formatIntent(row.content_intent)}</td>
                          <td className="py-3 px-3">
                            <span className={`inline-flex text-xs font-semibold px-2 py-1 rounded-full ${getPostTypeClass(row.post_type)}`}>
                              {row.post_type}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-sm text-gray-900 dark:text-white">{row.title}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 dark:text-gray-400">{row.cta}</td>
                          <td className="py-3 px-3 text-sm text-gray-600 dark:text-gray-400">{row.keyword_used || '—'}</td>
                          <td className="py-3 px-3">
                            {isDraft ? (
                              <button
                                onClick={() => scheduleContentPost(row)}
                                disabled={updateContentStatusMutation.isPending}
                                title="Schedule this planned post using its default calendar date."
                                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all cursor-pointer hover:scale-105 active:scale-95 disabled:opacity-60 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <i className="ri-calendar-schedule-line"></i>
                                Schedule
                              </button>
                            ) : (
                              <span
                                title={
                                  row.status === 'published'
                                    ? 'Published — live on your Google Business Profile.'
                                    : 'Scheduled — queued in Post Scheduler, will publish on its scheduled date.'
                                }
                                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                                  row.status === 'published'
                                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                    : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                                }`}
                              >
                                <i className={row.status === 'published' ? 'ri-check-line' : 'ri-time-line'}></i>
                                {row.status === 'published' ? 'Published' : 'Scheduled'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'kpi' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Actions for each KPI</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Practical activities to improve each active goal. Website actions are included where they support the KPI.
            </p>
          </div>
          {goalsLoading ? (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
            </div>
          ) : goalsError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
              <p>Failed to load KPI goals: {goalsError.message}</p>
              <button onClick={() => refetchGoals()} className="mt-3 rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700">
                Retry
              </button>
            </div>
          ) : goals.length === 0 ? (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center text-sm text-gray-500">
              Generate strategy goals first to create a KPI action plan.
            </div>
          ) : goals.map((goal) => (
            <div key={goal.goal_id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{goal.goal_type}</h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">Target: {goal.target}</span>
              </div>
              <ul className="space-y-2">
                {(KPI_ACTIONS[goal.kpi_type] || KPI_ACTIONS.default).map((action) => (
                  <li key={action} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <i className="ri-checkbox-blank-circle-line text-teal-600 mt-0.5" />
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
